/**
 * Iter XX.94 — Parent Entry (MVP).
 *
 * Design goal (founder brief): Gully learns who it's helping BEFORE
 * the first conversation, without forking the coaching engine.
 * Selecting "My child" persists a lightweight relationship signal;
 * the LLM directive on the backend then rewrites first-person
 * prompts into natural third-person language using the child's name.
 *
 * Detection rule
 *   · Show for EVERY fresh anonymous visitor on their first /chat
 *     mount. (No UTM gating — parents can arrive from any source.)
 *   · Signed-in visitors with a persisted relationship bypass.
 *   · Anyone who's already chosen (`g_parent_mode_v1` present in
 *     localStorage) bypasses forever.
 *
 * Persistence: `localStorage.g_parent_mode_v1` =
 *   `{ relationship: 'self' | 'parent', player_name?: string, ts }`
 * Consumed-and-attached to the first outbound /web/chat POST via
 * `consumeParentContextForNextTurn()` so the LLM directive fires on
 * the very first coaching turn.
 */

import { hasToken } from "./nativeTokenStorage";

const DONE_KEY = "g_parent_mode_v1";
const CONTEXT_KEY = "g_parent_mode_v1_context";
// Iter XX.96 — Pending intent stash. When the Parent Entry gate is
// triggered at the "commitment moment" (chip tap or Send on landing
// or on a direct /chat visit), the visitor's original message /
// entry-point is stashed here so /chat can resume it automatically
// after the gate completes and the XX.92 scripted intro plays.
// sessionStorage is intentional — pending intent must not survive
// a tab close, and must be one-shot: consumePendingIntent() clears
// it. Falls back gracefully if storage is disabled.
const PENDING_INTENT_KEY = "g_pending_intent_v1";

/** True if this visitor already picked a relationship (any mode). */
export function isParentGateCompleted() {
  try { return Boolean(localStorage.getItem(DONE_KEY)); }
  catch { return false; }
}

/** True if the visitor is already signed in (existing user). */
function _isSignedInLocal() {
  try { return hasToken(); }
  catch { return false; }
}

/**
 * Composite eligibility. Feature-flagging is out of scope for XX.94
 * (founder framed as pure onboarding change) — signed-in and
 * already-completed visitors are the only bypasses.
 */
export function shouldShowParentEntryGate() {
  if (_isSignedInLocal()) return false;
  if (isParentGateCompleted()) return false;
  // Iter XX.130 — QA bypass for automated Playwright / testing agent
  // flows that need to exercise IDE → PCE-card paths without going
  // through audience selection first. Consumers: sessionStorage
  // `g_qa_skip_parent_gate=1` OR URL param `?qa_no_parent_gate=1`.
  try {
    if (sessionStorage.getItem("g_qa_skip_parent_gate") === "1") return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("qa_no_parent_gate") === "1") {
      sessionStorage.setItem("g_qa_skip_parent_gate", "1");
      return false;
    }
  } catch { /* noop */ }
  return true;
}

/**
 * Persist the parent-mode selection. Also stashes the parent_context
 * so Chat.jsx can attach it to the FIRST /web/chat call.
 */
export function markParentGateCompleted({ relationship, player_name }) {
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify({
      relationship: relationship || "self",
      player_name: player_name || null,
      ts: Date.now(),
    }));
    if (relationship === "parent" && player_name) {
      localStorage.setItem(CONTEXT_KEY, JSON.stringify({
        relationship: "parent",
        player_name,
        ts: Date.now(),
      }));
    } else if (relationship === "self") {
      // No context to attach for the self path.
      try { localStorage.removeItem(CONTEXT_KEY); } catch { /* noop */ }
    }
  } catch { /* noop */ }
}

/** Read the stashed context (used for downstream rehydration checks). */
export function readStashedParentContext() {
  try {
    const raw = localStorage.getItem(CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.relationship !== "parent") return null;
    if (!parsed.player_name) return null;
    return {
      relationship: "parent",
      player_name: String(parsed.player_name).trim().slice(0, 40),
    };
  } catch { return null; }
}

/**
 * Consume the stashed parent context — returns it and deletes it in
 * one call, so it's only ever attached to the FIRST /web/chat request.
 * Callers should treat a null return as "nothing to attach".
 */
export function consumeStashedParentContext() {
  const ctx = readStashedParentContext();
  if (!ctx) return null;
  try { localStorage.removeItem(CONTEXT_KEY); } catch { /* noop */ }
  return ctx;
}

/** Read the persisted relationship (for UI display / persona-aware surfaces). */
export function readParentMode() {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/**
 * Iter XX.96 — Stash the visitor's pending intent (original message
 * they attempted to send when the Parent Entry gate was triggered).
 * Consumed by Chat.jsx on mount so the visitor never has to retype.
 *
 * @param {object} intent  { text: string, source?: string, kind?: "text" | "video" }
 */
export function stashPendingIntent(intent) {
  if (!intent || !intent.text) return;
  try {
    sessionStorage.setItem(PENDING_INTENT_KEY, JSON.stringify({
      text: String(intent.text),
      source: intent.source || null,
      kind: intent.kind || "text",
      // Iter XX.149 — Instant-navigate landing→chat handoff:
      // preserve the landing page id (for /web/session) and the
      // high-res tap timestamp (for the latency waterfall log) so
      // Chat.jsx can pick up exactly where the landing page left off.
      landing_page_id: intent.landing_page_id || null,
      t_tap: typeof intent.t_tap === "number" ? intent.t_tap : null,
      // Iter XX.172 — Focused Entry Chips experiment metadata.
      // Persisted on the intent so Chat.jsx can forward it to
      // /web/chat + tag every downstream funnel event with the same
      // arm/problem/situation. NEVER enters the LLM prompt.
      entry_experiment: intent.entry_experiment || null,
      entry_problem:    intent.entry_problem    || null,
      entry_situation:  intent.entry_situation  || null,
      // Iter XX.254 — deterministic starter hook. Populated only
      // when the landing chip declares an explicit starter_id
      // (currently: `got_out` → `keep_getting_out`). Chat.jsx
      // forwards it to /web/chat so the backend can trigger the
      // deterministic format-clarification hook without ever
      // keyword-matching prose.
      starter_id:       intent.starter_id       || null,
      ts: Date.now(),
    }));
  } catch { /* noop */ }
}

/**
 * Iter XX.96 — One-shot consume: return the stashed pending intent
 * and clear it in the same call. Callers should treat a null return
 * as "no pending intent to resume".
 */
export function consumePendingIntent() {
  try {
    const raw = sessionStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    try { sessionStorage.removeItem(PENDING_INTENT_KEY); } catch { /* noop */ }
    if (!parsed || !parsed.text) return null;
    return {
      text: String(parsed.text),
      source: parsed.source || null,
      kind: parsed.kind || "text",
      landing_page_id: parsed.landing_page_id || null,
      t_tap: typeof parsed.t_tap === "number" ? parsed.t_tap : null,
      // Iter XX.172 — surface the experiment metadata so Chat.jsx
      // can forward it on the first /web/chat call and tag funnel
      // events. Absent on non-chip / free-text entries.
      entry_experiment: parsed.entry_experiment || null,
      entry_problem:    parsed.entry_problem    || null,
      entry_situation:  parsed.entry_situation  || null,
      // Iter XX.254 — deterministic starter hook (see stash docs).
      starter_id:       parsed.starter_id       || null,
    };
  } catch { return null; }
}

/**
 * Iter XX.107 (hotfix) — Non-destructive peek. Returns the stashed
 * pending intent WITHOUT clearing sessionStorage.
 *
 * Why: React 18 StrictMode double-invokes effects on mount in
 * development. If Chat.jsx's bootstrap effect calls the destructive
 * `consumePendingIntent()`, the first (ephemeral) mount clears
 * storage and populates a ref that is then discarded on
 * StrictMode's simulated unmount. The real (second) mount finds
 * empty storage and drops the intent — original coaching message
 * lost, no /api/web/chat POST fires.
 *
 * Fix: bootstrap peeks, downstream flush actually consumes right
 * before firing send(). Storage stays populated until we're
 * certain we're about to replay. Multiple peek calls are safe.
 */
export function peekPendingIntent() {
  try {
    const raw = sessionStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.text) return null;
    return {
      text: String(parsed.text),
      source: parsed.source || null,
      kind: parsed.kind || "text",
      landing_page_id: parsed.landing_page_id || null,
      t_tap: typeof parsed.t_tap === "number" ? parsed.t_tap : null,
      // Iter XX.172 — same metadata as consumePendingIntent, exposed
      // on peek so read-only callers can inspect experiment arm
      // without draining the intent.
      entry_experiment: parsed.entry_experiment || null,
      entry_problem:    parsed.entry_problem    || null,
      entry_situation:  parsed.entry_situation  || null,
      // Iter XX.254 — deterministic starter hook (see stash docs).
      starter_id:       parsed.starter_id       || null,
    };
  } catch { return null; }
}
